import {ContentKey, FunnelKey, RoleKey} from "./Keys";
import {array_chunk, array_move} from "../utils/functions";
import {CellContent} from "./CellContent";
import {INITIAL_ROLE_CELL_DATA, RoleCellData} from "./RoleCell";


abstract class CellMatrix<CellT, ColumnKeyT, RowKeyT> {

    protected abstract createCell(): CellT;

    protected abstract cloneCell(original: CellT): CellT;

    private readonly column_keys: ColumnKeyT[];
    private readonly row_keys: RowKeyT[];
    private readonly cells: CellT[];

    constructor(cells: CellT[], column_keys: ColumnKeyT[], row_keys: RowKeyT[]) {
        if (column_keys.length * row_keys.length !== cells.length) {
            throw new Error("invalid cells length");
        }

        this.column_keys = column_keys;
        this.row_keys = row_keys;
        this.cells = cells;
    }

    get(column_key: ColumnKeyT, row_key: RowKeyT): CellT {
        const column_index = this.column_keys.findIndex(k => k === column_key);
        if (column_index === -1) {
            throw new Error(`no column_key: ${column_key}`);
        }
        const row_index = this.row_keys.findIndex(k => k === row_key);
        if (row_index === -1) {
            throw new Error(`no row_key: ${row_key}`);
        }

        return this.cells[this.columnAndRowIndexToCellIndex(column_index, row_index)];
    }

    each(callbackfn: (cell: CellT, column_key: ColumnKeyT, row_key: RowKeyT) => void): void {
        this.row_keys.forEach(((row_key, row_index) => {
            this.column_keys.forEach((column_key, column_index) => {
                callbackfn(this.cells[this.columnAndRowIndexToCellIndex(column_index, row_index)], column_key, row_key);
            })
        }));
    }

    map<U>(callbackfn: (cell: CellT, column_key: ColumnKeyT, row_key: RowKeyT) => U): U[] {
        const result: U[] = [];
        this.each((cell, column_key, row_key) =>
            result.push(callbackfn(cell, column_key, row_key))
        );
        return result;
    }


    clone(): CellT[] {
        return this.cells.map(c => this.cloneCell(c));
    }

    update(column_key: ColumnKeyT, row_key: RowKeyT, cell: CellT): CellT[] {
        return this.updateAll([{column_key, row_key}], cell);
    }

    updateAll(target: { column_key: ColumnKeyT, row_key: RowKeyT }[], cell: CellT): CellT[] {

        const update_indexes = target.map(t => {
            const column_index = this.column_keys.findIndex(k => k === t.column_key);
            if (column_index === -1) {
                throw new Error(`no column_key: ${t.column_key}`);
            }
            const row_index = this.row_keys.findIndex(k => k === t.row_key);
            if (row_index === -1) {
                throw new Error(`no row_key: ${t.row_key}`);
            }

            return this.columnAndRowIndexToCellIndex(column_index, row_index);
        });

        const update_cell = cell;
        return this.cells.map((cell, index) => {
            return update_indexes.some(i => i === index) ? update_cell : this.cloneCell(cell);
        })
    }

    appendColumn(position: number): CellT[] {
        if (position < 0 || this.column_keys.length < position) {
            throw new Error(`invalid column position: ${position}`);
        }

        const new_cells: CellT[] = [];
        const funnel_length = this.column_keys.length;
        for (let i = 0; i < this.cells.length; ++i) {

            const cells_funnel_index = i % funnel_length;
            if (cells_funnel_index === position) {
                new_cells.push(this.createCell());
            }
            new_cells.push(this.cloneCell(this.cells[i]));

            if (funnel_length === position
                && cells_funnel_index === funnel_length - 1) {
                new_cells.push(this.createCell());
            }
        }

        return new_cells;
    }

    appendRow(position: number): CellT[] {
        if (position < 0 || this.row_keys.length < position) {
            throw new Error(`invalid row position: ${position}`);
        }

        const before_cells: CellT[] = [];
        const after_cells: CellT[] = [];
        for (let i = 0; i < this.cells.length; ++i) {
            const cells_row_index = this.cellIndexToRowIndex(i);
            if (cells_row_index < position) {
                before_cells.push(this.cloneCell(this.cells[i]));
            } else {
                after_cells.push(this.cloneCell(this.cells[i]));
            }
        }

        const append_cells: CellT[] = this.column_keys.map(
            funnel_key => this.createCell()
        );

        return before_cells.concat(append_cells, after_cells);
    }

    deleteColumn(index: number): CellT[] {
        if (index < 0 || this.column_keys.length <= index) {
            throw new Error(`invalid column index: ${index}`);
        }

        const new_cells: CellT[] = [];
        for (let i = 0; i < this.cells.length; ++i) {
            if (this.cellIndexToColumnIndex(i) !== index) {
                new_cells.push(this.cloneCell(this.cells[i]));
            }
        }
        return new_cells;
    }

    deleteRow(index: number): CellT[] {
        if (index < 0 || this.row_keys.length <= index) {
            throw new Error(`invalid row index: ${index}`);
        }

        const new_cells: CellT[] = [];
        for (let i = 0; i < this.cells.length; ++i) {
            if (this.cellIndexToRowIndex(i) !== index) {
                new_cells.push(this.cloneCell(this.cells[i]));
            }
        }

        return new_cells;
    }

    moveColumn(from_index: number, to_index: number): CellT[] {
        if (this.cells.length === 0) {
            // avoid TypeError: Reduce of empty array with no initial value (at reduce)
            return [];
        }
        return array_chunk(
            this.cells,
            this.column_keys.length,
            chunk => array_move(chunk, from_index, to_index)
        ).reduce((acc, cur) => acc.concat(cur));
    }

    moveRow(from_index: number, to_index: number): CellT[] {
        if (this.cells.length === 0) {
            return [];
        }
        return array_move(
            array_chunk(this.cells, this.column_keys.length), from_index, to_index
        ).reduce((acc, cur) => acc.concat(cur));
    }

    toJSON() {
        return this.cells;
    }

    private cellIndexToRowIndex(cell_index: number): number {
        return Math.floor(cell_index / this.column_keys.length);
    }

    private cellIndexToColumnIndex(cell_index: number): number {
        return cell_index % this.column_keys.length;
    }

    private columnAndRowIndexToCellIndex(column_index: number, row_index: number): number {
        return (row_index * this.column_keys.length) + column_index;
    }
}

export type ContentCellData = CellContent;

export class ContentCellMatrix extends CellMatrix<ContentCellData, FunnelKey, ContentKey> {
    protected createCell(): ContentCellData {
        return CellContent.createNew();
    }

    protected cloneCell(original: ContentCellData): ContentCellData {
        return CellContent.from(original);
    }

}

export class RoleCellMatrix extends CellMatrix<RoleCellData, FunnelKey, RoleKey> {
    protected createCell(): RoleCellData {
        return INITIAL_ROLE_CELL_DATA;
    }

    protected cloneCell(original: RoleCellData): RoleCellData {
        return original;
    }

}
